import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, 
  ArrowUpRight, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  AlertCircle,
  History,
  Ban,
  Smartphone,
  CreditCard,
  X,
  ChevronRight,
  RotateCcw,
  Zap
} from 'lucide-react';
import { TonConnectButton, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { withdrawApi, authApi, paymentApi, PaymentMethod, faucetPayApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useTelegram } from '../../hooks';
import toast from 'react-hot-toast';
import { FaucetPayWithdrawal } from '../FaucetPayWithdrawal/FaucetPayWithdrawal';

type WithdrawalStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'approved' | 'rejected' | 'cancelled' | 'refunded';
type WithdrawalType = 'auto' | 'manual' | 'faucetpay';

interface WithdrawalHistory {
  id: string;
  amount: number;
  tonAmount: number;
  netAmount?: number;
  fee?: number;
  status: WithdrawalStatus;
  withdrawalType: WithdrawalType;
  paymentMethodName?: string;
  txHash?: string;
  transactionHash?: string;
  walletAddress?: string;
  adminNotes?: string;
  createdAt: string | Date;
  // Local currency fields for data consistency
  localCurrencyAmount?: number;
  localCurrency?: string;
  // FaucetPay specific fields
  coin?: string;
  faucetPayEmail?: string;
}

type TabType = 'ton' | 'manual' | 'faucetpay';

export function Withdrawal() {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('manual');
  
  // Common state
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<WithdrawalHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [withdrawalEnabled, setWithdrawalEnabled] = useState(true);
  const [automaticEnabled, setAutomaticEnabled] = useState(true); // TON wallet withdrawal
  const [manualEnabled, setManualEnabled] = useState(true);       // Manual withdrawal methods
  const [faucetPayEnabled, setFaucetPayEnabled] = useState(false); // FaucetPay instant withdrawal
  const [configLoaded, setConfigLoaded] = useState(false);

  // TON withdrawal state
  const [estimatedTon, setEstimatedTon] = useState(0);
  const [feePercent, setFeePercent] = useState(0);
  const [minWithdraw, setMinWithdraw] = useState(50000);
  const [conversionRate, setConversionRate] = useState(5000); // Default: 100k points = 1 TON

  // Manual withdrawal state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<Record<string, string>>({});
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(true);

  const [_tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const user = useAppStore((state) => state.user);
  const updateBalance = useAppStore((state) => state.updateBalance);
  const withdrawalConfig = useConfigStore((state) => state.withdrawal);
  const { hapticFeedback, hapticNotification } = useTelegram();
  
  const savedWalletRef = useRef<string | null>(null);

  // Log user state for debugging
  useEffect(() => {
    console.log('[Withdrawal] User state:', { 
      userId: user?.telegramId, 
      balance: user?.balance,
      hasUser: !!user 
    });
  }, [user]);

  useEffect(() => {
    fetchSettings();
    fetchHistory();
    fetchPaymentMethods();
    fetchFaucetPayConfig();
  }, []);

  // Save wallet address to backend when TON Connect wallet is connected
  useEffect(() => {
    const saveWalletToBackend = async () => {
      if (wallet?.account?.address && wallet.account.address !== savedWalletRef.current) {
        try {
          await authApi.updateWallet(wallet.account.address);
          savedWalletRef.current = wallet.account.address;
        } catch (error) {
          console.error('Failed to save wallet address:', error);
        }
      }
    };
    saveWalletToBackend();
  }, [wallet]);

  const fetchSettings = async () => {
    try {
      const settings = await withdrawApi.getSettings() as {
        minTonAmount: number;
        platformFeePercent: number;
        pointsToTonRate: number;
        withdrawalEnabled?: boolean;
        automaticEnabled?: boolean;
        manualEnabled?: boolean;
      };
      console.log('[Withdrawal] Settings loaded:', settings);
      setMinWithdraw(settings.minTonAmount || 50000);
      
      // Validate and normalize the conversion rate
      // Rate should be positive and represent "points per TON"
      const rawRate = settings.pointsToTonRate;
      let validRate = 100000; // Default: 100k points = 1 TON
      if (rawRate && Number.isFinite(rawRate) && rawRate > 0) {
        // If rate is less than 1, it might be in "TON per point" format, convert it
        validRate = rawRate < 1 ? Math.round(1 / rawRate) : rawRate;
      }
      setConversionRate(validRate);
      
      setFeePercent(settings.platformFeePercent || 0);
      setWithdrawalEnabled(
        settings.withdrawalEnabled !== undefined 
          ? settings.withdrawalEnabled 
          : withdrawalConfig.enabled
      );
      setAutomaticEnabled(settings.automaticEnabled ?? true);
      setManualEnabled(settings.manualEnabled ?? true);
      
      // Set default active tab based on what's enabled
      if (!settings.manualEnabled && settings.automaticEnabled) {
        setActiveTab('ton');
      } else if (settings.manualEnabled) {
        setActiveTab('manual');
      }
      
      setConfigLoaded(true);
    } catch (error) {
      console.error('[Withdrawal] Failed to fetch settings:', error);
      setWithdrawalEnabled(withdrawalConfig.enabled);
      setConfigLoaded(true);
    }
  };

  const fetchHistory = async () => {
    try {
      // Fetch both regular withdrawals and FaucetPay withdrawals
      const [regularData, faucetPayData] = await Promise.all([
        withdrawApi.getHistory(),
        faucetPayApi.getHistory(1, 50).catch(() => ({ withdrawals: [] }))
      ]);
      
      console.log('[Withdrawal] Regular history loaded:', regularData);
      console.log('[Withdrawal] FaucetPay history loaded:', faucetPayData);
      
      // Transform FaucetPay withdrawals to match WithdrawalHistory interface
      const faucetPayWithdrawals: WithdrawalHistory[] = (faucetPayData.withdrawals || []).map((w: any) => ({
        id: w._id || w.id,
        amount: w.pointsAmount,
        tonAmount: 0, // FaucetPay doesn't use TON
        netAmount: w.netAmount,
        fee: w.feeAmount,
        status: w.status as WithdrawalStatus,
        withdrawalType: 'faucetpay' as WithdrawalType,
        paymentMethodName: `FaucetPay (${w.coin})`,
        walletAddress: w.faucetPayEmail,
        transactionHash: w.faucetPayPayoutId,
        createdAt: w.createdAt,
        coin: w.coin,
        faucetPayEmail: w.faucetPayEmail,
      }));
      
      // Combine and sort by date (newest first)
      const allWithdrawals = [
        ...(regularData.withdrawals as WithdrawalHistory[]),
        ...faucetPayWithdrawals
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setHistory(allWithdrawals);
    } catch (error) {
      console.error('[Withdrawal] Failed to fetch history:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    setLoadingMethods(true);
    try {
      const data = await paymentApi.getMethods();
      console.log('[Withdrawal] Payment methods response:', data);
      
      // Handle both response formats
      const methods = data.methods || (Array.isArray(data) ? data : []);
      console.log('[Withdrawal] Parsed methods:', methods);
      
      setPaymentMethods(methods);
      if (methods.length > 0 && !selectedMethod) {
        setSelectedMethod(methods[0]);
      }
    } catch (error: unknown) {
      console.error('[Withdrawal] Failed to fetch payment methods:', error);
      const err = error as { response?: { status?: number; data?: { error?: string } } };
      
      // Don't show toast for 401 errors in dev mode (expected when using mock token)
      const is401 = err.response?.status === 401;
      const isDev = import.meta.env.DEV;
      
      if (!is401 || !isDev) {
        toast.error(err.response?.data?.error || 'Failed to load payment methods');
      } else {
        console.log('[Withdrawal] Skipping error toast for 401 in dev mode');
      }
    } finally {
      setLoadingMethods(false);
    }
  };

  const fetchFaucetPayConfig = async () => {
    try {
      const config = await faucetPayApi.getConfig();
      console.log('[Withdrawal] FaucetPay config:', config);
      setFaucetPayEnabled(config.enabled && !config.emergencyStop);
    } catch (error) {
      console.log('[Withdrawal] FaucetPay not available:', error);
      setFaucetPayEnabled(false);
    }
  };

  // Calculate estimated TON for TON withdrawal
  useEffect(() => {
    const numAmount = parseInt(amount) || 0;
    // Use safe rate with fallback
    const safeRate = (conversionRate && Number.isFinite(conversionRate) && conversionRate > 0) 
      ? conversionRate 
      : 100000;
    
    if (numAmount >= minWithdraw) {
      // conversionRate is now always in "points per TON" format
      const gross = Math.max(0, numAmount / safeRate);
      const feeAmount = Math.max(0, gross * (feePercent / 100));
      const netTon = Math.max(0, gross - feeAmount);
      setEstimatedTon(netTon);
    } else {
      setEstimatedTon(0);
    }
  }, [amount, conversionRate, feePercent, minWithdraw]);

  // Calculate estimated amount for manual withdrawal
  const manualWithdrawalEstimate = useMemo(() => {
    if (!selectedMethod || !amount) return null;
    
    const numAmount = parseInt(amount) || 0;
    if (numAmount < selectedMethod.minWithdrawal) return null;

    // Ensure conversionRate is valid (positive and finite)
    const safeRate = (conversionRate && Number.isFinite(conversionRate) && conversionRate > 0) 
      ? conversionRate 
      : 100000; // Fallback: 100k points = 1 TON
    
    // conversionRate is now always in "points per TON" format
    const tonEquivalent = Math.max(0, numAmount / safeRate);
    
    let fee: number;
    if (selectedMethod.feeType === 'percentage') {
      fee = Math.max(0, tonEquivalent * (selectedMethod.fee / 100));
    } else {
      fee = Math.max(0, selectedMethod.fee || 0);
    }
    
    const netAmount = Math.max(0, tonEquivalent - fee);
    const localAmount = Math.max(0, netAmount * (selectedMethod.conversionRate || 0));
    
    // Determine local currency based on payment method (case-insensitive)
    const methodNameLower = selectedMethod.name.toLowerCase();
    const isBDT = methodNameLower.includes('bkash') || 
                  methodNameLower.includes('nagad') || 
                  methodNameLower.includes('rocket') ||
                  methodNameLower.includes('bangladesh');
    
    return {
      gross: tonEquivalent,
      fee,
      net: netAmount,
      localAmount,
      localCurrency: isBDT ? 'BDT' : 'USD'
    };
  }, [amount, selectedMethod, conversionRate]);

  const handleTonWithdraw = async () => {
    if (!withdrawalEnabled) {
      toast.error(<span>Withdrawals are currently disabled. <a href="/support" className="underline text-blue-300 ml-1">Contact support</a></span>);
      return;
    }

    if (!wallet) {
      toast.error('Please connect your wallet first');
      return;
    }

    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < minWithdraw) {
      toast.error(`Minimum withdrawal is ${minWithdraw.toLocaleString()} points`);
      return;
    }

    if (user && numAmount > user.balance) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    hapticFeedback('medium');

    try {
      const result = await withdrawApi.request(numAmount);

      if (result.success) {
        hapticNotification('success');
        toast.success('Withdrawal request submitted!');
        
        // Use server-returned balance for accuracy
        if (result.newBalance !== undefined) {
          updateBalance(result.newBalance);
        } else if (user) {
          // Fallback only if server doesn't return balance
          updateBalance(user.balance - numAmount);
        }
        
        setAmount('');
        fetchHistory();
      }
    } catch (error: unknown) {
      hapticNotification('error');
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManualWithdraw = async () => {
    if (!withdrawalEnabled) {
      toast.error(<span>Withdrawals are currently disabled. <a href="/support" className="underline text-blue-300 ml-1">Contact support</a></span>);
      return;
    }

    if (!selectedMethod) {
      toast.error('Please select a payment method');
      return;
    }

    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < selectedMethod.minWithdrawal) {
      toast.error(`Minimum withdrawal for ${selectedMethod.name} is ${selectedMethod.minWithdrawal.toLocaleString()} points`);
      return;
    }

    if (numAmount > selectedMethod.maxWithdrawal) {
      toast.error(`Maximum withdrawal for ${selectedMethod.name} is ${selectedMethod.maxWithdrawal.toLocaleString()} points`);
      return;
    }

    if (user && numAmount > user.balance) {
      toast.error('Insufficient balance');
      return;
    }

    for (const field of selectedMethod.requiredFields) {
      if (field.required && (!paymentDetails[field.name] || paymentDetails[field.name].trim() === '')) {
        toast.error(`${field.label} is required`);
        return;
      }
      
      if (field.validation && paymentDetails[field.name]) {
        const regex = new RegExp(field.validation);
        if (!regex.test(paymentDetails[field.name])) {
          toast.error(`Invalid ${field.label} format`);
          return;
        }
      }
    }

    setLoading(true);
    hapticFeedback('medium');

    try {
      // Pass the calculated local currency values that user sees
      const calculatedValues = manualWithdrawalEstimate ? {
        localCurrencyAmount: manualWithdrawalEstimate.localAmount,
        localCurrency: manualWithdrawalEstimate.localCurrency,
      } : undefined;

      const result = await paymentApi.requestManualWithdrawal(
        numAmount,
        selectedMethod._id,
        paymentDetails,
        calculatedValues
      );

      if (result.success) {
        hapticNotification('success');
        toast.success(result.message || 'Withdrawal request submitted!');
        
        // Use server-returned balance for accuracy
        if (result.newBalance !== undefined) {
          updateBalance(result.newBalance);
        } else if (user) {
          // Fallback only if server doesn't return balance
          updateBalance(user.balance - numAmount);
        }
        
        setAmount('');
        setPaymentDetails({});
        fetchHistory();
      } else {
        toast.error(result.error || 'Withdrawal failed');
      }
    } catch (error: unknown) {
      hapticNotification('error');
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelWithdrawal = async (withdrawalId: string) => {
    if (!confirm('Are you sure you want to cancel this withdrawal?')) return;

    try {
      const result = await paymentApi.cancelWithdrawal(withdrawalId);
      if (result.success) {
        toast.success('Withdrawal cancelled and points refunded');
        fetchHistory();
        window.location.reload();
      } else {
        toast.error(result.error || 'Failed to cancel');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to cancel');
    }
  };

  const getStatusIcon = (status: WithdrawalStatus) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="w-3 h-3" />;
      case 'pending':
      case 'processing':
        return <Clock className="w-3 h-3" />;
      case 'failed':
      case 'rejected':
        return <XCircle className="w-3 h-3" />;
      case 'cancelled':
        return <Ban className="w-3 h-3" />;
      case 'refunded':
        return <RotateCcw className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getStatusBadgeStyle = (status: WithdrawalStatus) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'approved':
        return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'processing':
        return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      case 'failed':
      case 'rejected':
        return 'bg-red-500/20 text-red-400 border border-red-500/30';
      case 'cancelled':
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
      case 'refunded':
        return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
      default:
        return 'bg-white/10 text-white/60';
    }
  };

  const quickAmounts = [10000, 50000, 100000, 250000];

  // Check if both withdrawal methods are disabled
  const bothMethodsDisabled = !automaticEnabled && !manualEnabled;

  // Check if user is banned
  const isBanned = user?.isBanned;

  // Show loading state while config is being fetched
  if (!configLoaded) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card p-8 flex flex-col items-center justify-center min-h-[300px]"
      >
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-white/60">Loading withdrawal settings...</p>
      </motion.div>
    );
  }

  // Show notice when user is banned
  if (isBanned) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-4"
      >
        {/* Balance card */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white/60 text-xs">Balance (Frozen)</p>
                <p className="font-bold text-xl text-white/50">{user?.balance.toLocaleString() || 0}</p>
              </div>
            </div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <History className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Account Suspended Notice */}
        <motion.div 
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="card p-6 text-center border border-red-500/30"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center">
            <Ban className="w-10 h-10 text-red-400" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-red-400">Account Suspended</h3>
          <p className="text-white/60 mb-4">
            Your account has been suspended and withdrawals are not available.
            {user?.banReason && (
              <span className="block mt-2 text-white/40 text-sm">
                Reason: {user.banReason}
              </span>
            )}
          </p>
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Contact support if you believe this is a mistake</span>
            </div>
          </div>
        </motion.div>

        {/* History section - still accessible */}
        <AnimatePresence>
          {showHistory && history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="card p-4 overflow-hidden"
            >
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Withdrawal History
              </h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {history.slice(0, 10).map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-lg bg-white/5 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(item.status)}
                      <div>
                        <p className="text-sm font-medium">
                          {item.amount.toLocaleString()} points
                        </p>
                        <p className="text-xs text-white/40">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeStyle(item.status)}`}>
                      {item.status}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // Show notice when both withdrawal methods are disabled
  if (!withdrawalEnabled || bothMethodsDisabled) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-4"
      >
        {/* Balance card */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white/60 text-xs">Available Balance</p>
                <p className="font-bold text-xl">{user?.balance.toLocaleString() || 0}</p>
                {user && conversionRate > 0 && (
                  <p className="text-xs text-white/40">
                    ≈ {Math.max(0, user.balance / conversionRate).toFixed(4)} TON
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <History className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Withdrawal Unavailable Notice */}
        <motion.div 
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="card p-6 text-center"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
            <Ban className="w-10 h-10 text-orange-400" />
          </div>
          <h3 className="text-xl font-bold mb-2">Withdrawals Unavailable</h3>
          <p className="text-white/60 mb-4">
            Withdrawal services are currently under maintenance. We're working to bring them back as soon as possible.
          </p>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-center gap-2 text-white/50 text-sm">
              <Clock className="w-4 h-4" />
              <span>Please check back later</span>
            </div>
          </div>
        </motion.div>

        {/* History section - still accessible */}
        <AnimatePresence>
          {showHistory && history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="card p-4 overflow-hidden"
            >
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Withdrawal History
              </h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {history.slice(0, 10).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-xl bg-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        item.withdrawalType === 'auto' ? 'bg-blue-500/20' : 
                        item.withdrawalType === 'faucetpay' ? 'bg-green-500/20' : 'bg-purple-500/20'
                      }`}>
                        {item.withdrawalType === 'auto' ? (
                          <CreditCard className="w-4 h-4 text-blue-400" />
                        ) : item.withdrawalType === 'faucetpay' ? (
                          <Zap className="w-4 h-4 text-green-400" />
                        ) : (
                          <Smartphone className="w-4 h-4 text-purple-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {item.amount.toLocaleString()} pts
                          {item.withdrawalType === 'faucetpay' && item.coin && (
                            <span className="text-green-400 ml-1">→ {item.coin}</span>
                          )}
                        </p>
                        <p className="text-xs text-white/50">
                          {new Date(item.createdAt).toLocaleDateString()}
                          {item.withdrawalType === 'faucetpay' && ' • FaucetPay'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${getStatusBadgeStyle(item.status)}`}>
                      {getStatusIcon(item.status)}
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Balance card */}
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/60 text-xs">Available Balance</p>
            <p className="font-bold text-xl">{user?.balance.toLocaleString() || 0}</p>
            {user && conversionRate > 0 && (
              <p className="text-xs text-white/40">
                ≈ {Math.max(0, user.balance / conversionRate).toFixed(4)} TON
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
        {manualEnabled && (
          <button
            onClick={() => { setActiveTab('manual'); setShowHistory(false); }}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'manual' && !showHistory
                ? 'bg-primary text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            <span>Manual</span>
          </button>
        )}
        {faucetPayEnabled && (
          <button
            onClick={() => { setActiveTab('faucetpay'); setShowHistory(false); }}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'faucetpay' && !showHistory
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Zap className="w-4 h-4" />
            <span>Instant</span>
          </button>
        )}
        {automaticEnabled && (
          <button
            onClick={() => { setActiveTab('ton'); setShowHistory(false); }}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'ton' && !showHistory
                ? 'bg-primary text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            <span>TON</span>
          </button>
        )}
        <button
          onClick={() => setShowHistory(true)}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            showHistory
              ? 'bg-primary text-white'
              : 'text-white/60 hover:text-white'
          }`}
        >
          <History className="w-4 h-4" />
          <span>History</span>
          {history.length > 0 && (
            <span className="w-5 h-5 rounded-full bg-primary/30 text-xs flex items-center justify-center">
              {history.length > 9 ? '9+' : history.length}
            </span>
          )}
        </button>
      </div>

      {/* Manual Withdrawal Tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'manual' && manualEnabled && !showHistory && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="card p-4"
          >
            <h3 className="font-bold text-lg mb-4">Manual Withdrawal</h3>

            {loadingMethods ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8 text-white/50">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No payment methods available</p>
              </div>
            ) : (
              <>
                {/* Payment method selector */}
                <div className="mb-4">
                  <label className="text-sm text-white/60 mb-2 block">Payment Method</label>
                  <button
                    onClick={() => setShowMethodSelector(true)}
                    className="w-full flex items-center justify-between p-3 rounded-xl bg-white/10 hover:bg-white/15 transition-colors"
                  >
                    {selectedMethod ? (
                      <div className="flex items-center gap-3">
                        {selectedMethod.logo ? (
                          <img 
                            src={selectedMethod.logo} 
                            alt={selectedMethod.name}
                            className="w-8 h-8 rounded-lg object-contain bg-white"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <div className="text-left">
                          <p className="font-medium">{selectedMethod.name}</p>
                          <p className="text-xs text-white/50">{selectedMethod.processingTime}</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-white/50">Select payment method</span>
                    )}
                    <ChevronRight className="w-5 h-5 text-white/50" />
                  </button>
                </div>

                {selectedMethod && (
                  <>
                    {/* Amount input */}
                    <div className="mb-4">
                      <label className="text-sm text-white/60 mb-2 block">Amount (Points)</label>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder={`Min: ${selectedMethod.minWithdrawal.toLocaleString()}`}
                        className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-lg font-medium focus:outline-none focus:border-primary"
                      />
                      <div className="flex justify-between text-xs text-white/40 mt-1">
                        <span>Min: {selectedMethod.minWithdrawal.toLocaleString()}</span>
                        <span>Max: {selectedMethod.maxWithdrawal.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Quick amount buttons */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {quickAmounts.map((quickAmount) => (
                        <motion.button
                          key={quickAmount}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setAmount(quickAmount.toString())}
                          className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                            amount === quickAmount.toString()
                              ? 'bg-primary text-white'
                              : 'bg-white/10 hover:bg-white/20'
                          }`}
                        >
                          {quickAmount >= 1000 ? `${quickAmount / 1000}k` : quickAmount}
                        </motion.button>
                      ))}
                    </div>

                    {/* Dynamic form fields */}
                    <div className="space-y-3 mb-4">
                      {selectedMethod.requiredFields.map((field) => (
                        <div key={field.name}>
                          <label className="text-sm text-white/60 mb-1 block">
                            {field.label} {field.required && <span className="text-red-400">*</span>}
                          </label>
                          {field.type === 'select' && field.options ? (
                            <select
                              value={paymentDetails[field.name] || ''}
                              onChange={(e) => setPaymentDetails({ ...paymentDetails, [field.name]: e.target.value })}
                              className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-primary"
                            >
                              <option value="">Select {field.label}</option>
                              {field.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type}
                              value={paymentDetails[field.name] || ''}
                              onChange={(e) => setPaymentDetails({ ...paymentDetails, [field.name]: e.target.value })}
                              placeholder={field.placeholder}
                              className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-primary"
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Instructions */}
                    {selectedMethod.instructions && (
                      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-4">
                        <p className="text-sm text-blue-200">{selectedMethod.instructions}</p>
                      </div>
                    )}

                    {/* Estimate */}
                    {manualWithdrawalEstimate && (
                      <div className="bg-white/5 rounded-xl p-3 mb-4 space-y-2 text-sm">
                        {/* <div className="flex justify-between">
                          <span className="text-white/60">Exchange Rate</span>
                          <span>{conversionRate.toLocaleString()} points = 1 TON</span>
                        </div> */}
                        <div className="flex justify-between">
                          <span className="text-white/60">TON Equivalent</span>
                          <span>{manualWithdrawalEstimate.gross.toFixed(4)} TON</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/60">Fee ({selectedMethod.feeType === 'percentage' ? `${selectedMethod.fee}%` : 'Fixed'})</span>
                          <span className="text-red-400">-{manualWithdrawalEstimate.fee.toFixed(4)} TON</span>
                        </div>
                        <div className="h-px bg-white/10" />
                        <div className="flex justify-between font-medium">
                          <span className="text-white/60">You'll receive</span>
                          <span className="text-green-400">
                            {manualWithdrawalEstimate.net.toFixed(4)} TON
                            {manualWithdrawalEstimate.localAmount > 0 && (
                              <span className="text-white/50 ml-1">
                                (~{manualWithdrawalEstimate.localAmount.toFixed(2)} {manualWithdrawalEstimate.localCurrency})
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Submit button */}
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleManualWithdraw}
                      disabled={loading || !selectedMethod || !amount || parseInt(amount) < selectedMethod.minWithdrawal || !withdrawalEnabled}
                      className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 ${
                        selectedMethod && amount && parseInt(amount) >= selectedMethod.minWithdrawal && withdrawalEnabled
                          ? 'btn-primary'
                          : 'bg-white/10 text-white/50 cursor-not-allowed'
                      }`}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <ArrowUpRight className="w-5 h-5" />
                          Request Withdrawal
                        </>
                      )}
                    </motion.button>

                    <p className="text-xs text-white/40 text-center mt-3">
                      Manual withdrawals are reviewed by admin ({selectedMethod.processingTime})
                    </p>
                  </>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* FaucetPay Instant Withdrawal Tab */}
        {activeTab === 'faucetpay' && faucetPayEnabled && !showHistory && (
          <motion.div
            key="faucetpay"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FaucetPayWithdrawal />
          </motion.div>
        )}

        {/* TON Withdrawal Tab */}
        {activeTab === 'ton' && automaticEnabled && !showHistory && (
          <motion.div
            key="ton"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="card p-4"
          >
            <h3 className="font-bold text-lg mb-4">Withdraw to TON Wallet</h3>

            {/* Wallet connection */}
            <div className="mb-4">
              <label className="text-sm text-white/60 mb-2 block">TON Wallet</label>
              <TonConnectButton />
            </div>

            {wallet && (
              <p className="text-xs text-white/40 truncate mb-4">
                Connected: {wallet.account.address.slice(0, 8)}...{wallet.account.address.slice(-8)}
              </p>
            )}

            {/* Amount input */}
            <div className="mb-4">
              <label className="text-sm text-white/60 mb-2 block">Amount (Points)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`Min: ${minWithdraw.toLocaleString()}`}
                className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-lg font-medium focus:outline-none focus:border-primary"
              />
            </div>

            {/* Quick amount buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {quickAmounts.map((quickAmount) => (
                <motion.button
                  key={quickAmount}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setAmount(quickAmount.toString())}
                  className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                    amount === quickAmount.toString()
                      ? 'bg-primary text-white'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {quickAmount >= 1000 ? `${quickAmount / 1000}k` : quickAmount}
                </motion.button>
              ))}
            </div>

            {/* Estimate */}
            <div className="bg-white/5 rounded-xl p-3 mb-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">Exchange Rate</span>
                <span>{conversionRate.toLocaleString()} points = 1 TON</span>
              </div>
              {amount && parseInt(amount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/60">TON Equivalent</span>
                  <span>{(parseInt(amount) / conversionRate).toFixed(4)} TON</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/60">Platform Fee</span>
                <span className="text-red-400">-{feePercent}%</span>
              </div>
              <div className="h-px bg-white/10" />
              <div className="flex justify-between font-medium">
                <span className="text-white/60">You'll receive</span>
                <span className="text-green-400">{estimatedTon.toFixed(4)} TON</span>
              </div>
            </div>

            {/* Withdraw button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleTonWithdraw}
              disabled={loading || !wallet || !amount || parseInt(amount) < minWithdraw || !withdrawalEnabled}
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 ${
                wallet && amount && parseInt(amount) >= minWithdraw && withdrawalEnabled
                  ? 'btn-primary'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowUpRight className="w-5 h-5" />
                  Withdraw
                </>
              )}
            </motion.button>

            {/* Warning */}
            {!wallet && (
              <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-200">
                  Connect your TON wallet to withdraw
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Withdrawal history */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-4 overflow-hidden"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Withdrawal History</h3>
              <button 
                onClick={fetchHistory}
                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
              >
                <Clock className="w-3 h-3" />
                Refresh
              </button>
            </div>

            {history.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 mx-auto mb-3 text-white/20" />
                <p className="text-white/50">No withdrawals yet</p>
                <p className="text-white/30 text-xs mt-1">Your withdrawal history will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item) => (
                  <div key={item.id} className="p-3 rounded-xl bg-white/5 border border-white/5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{item.amount.toLocaleString()} pts</p>
                          {item.withdrawalType === 'manual' && item.paymentMethodName && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                              {item.paymentMethodName}
                            </span>
                          )}
                          {item.withdrawalType === 'auto' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                              TON Wallet
                            </span>
                          )}
                          {item.withdrawalType === 'faucetpay' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                              FaucetPay ({item.coin})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-white/40 text-xs">
                            {new Date(item.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {item.withdrawalType === 'faucetpay' && item.faucetPayEmail && (
                            <p className="text-white/30 text-xs truncate max-w-[150px]">
                              → {item.faucetPayEmail}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {/* Show FaucetPay coin amount */}
                        {item.withdrawalType === 'faucetpay' ? (
                          <>
                            <p className="font-semibold text-green-400">
                              {(item.netAmount || 0).toFixed(8)} {item.coin}
                            </p>
                            {item.fee !== undefined && item.fee > 0 && (
                              <p className="text-xs text-white/40">
                                Fee: {item.fee.toFixed(8)}
                              </p>
                            )}
                          </>
                        ) : item.withdrawalType === 'manual' && item.localCurrencyAmount ? (
                          <>
                            <p className="font-semibold text-green-400">
                              {item.localCurrency === 'BDT' ? '৳' : '$'}
                              {item.localCurrencyAmount.toFixed(2)} {item.localCurrency}
                            </p>
                            <p className="text-xs text-white/40">
                              {item.tonAmount?.toFixed(4) || '0.0000'} TON
                            </p>
                          </>
                        ) : (
                          <p className="font-semibold text-green-400">
                            {item.tonAmount?.toFixed(4) || '0.0000'} TON
                          </p>
                        )}
                        <div className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-1 ${getStatusBadgeStyle(item.status)}`}>
                          {getStatusIcon(item.status)}
                          <span className="capitalize">{item.status}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Status-specific messages */}
                    {item.status === 'pending' && (
                      <div className="mt-2 p-2 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <p className="text-xs text-yellow-300">⏳ Waiting for admin review</p>
                      </div>
                    )}
                    
                    {item.status === 'approved' && (
                      <div className="mt-2 p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <p className="text-xs text-blue-300">✓ Approved! Payment will be sent soon</p>
                      </div>
                    )}
                    
                    {item.status === 'completed' && item.transactionHash && (
                      <div className="mt-2 p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                        <p className="text-xs text-green-300 break-all">Tx: {item.transactionHash}</p>
                      </div>
                    )}
                    
                    {item.status === 'rejected' && item.adminNotes && (
                      <div className="mt-2 p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                        <p className="text-xs text-red-300">❌ Reason: {item.adminNotes}</p>
                      </div>
                    )}
                    
                    {item.status === 'pending' && item.withdrawalType === 'manual' && (
                      <button
                        onClick={() => handleCancelWithdrawal(item.id)}
                        className="mt-2 text-xs text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                      >
                        <X className="w-3 h-3" />
                        Cancel & Refund
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info - hide when viewing history */}
      {!showHistory && (
        <div className="card p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-white/60">
              <p className="mb-2">Withdrawal Information:</p>
              <ul className="list-disc list-inside space-y-1 text-white/40 text-xs">
                <li>Manual withdrawals are reviewed by admin</li>
                <li>TON withdrawals are processed automatically</li>
                <li>Processing time varies by payment method</li>
                <li>Pending manual withdrawals can be cancelled</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Selector Modal */}
      <AnimatePresence>
        {showMethodSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
            onClick={() => setShowMethodSelector(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="w-full max-w-lg bg-dark rounded-t-3xl p-4 pb-20 max-h-[80vh] overflow-y-auto safe-area-inset-bottom"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Select Payment Method</h3>
                <button
                  onClick={() => setShowMethodSelector(false)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {paymentMethods.map((method) => (
                  <button
                    key={method._id}
                    onClick={() => {
                      setSelectedMethod(method);
                      setPaymentDetails({});
                      setShowMethodSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      selectedMethod?._id === method._id
                        ? 'bg-primary/20 border border-primary'
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    {method.logo ? (
                      <img 
                        src={method.logo} 
                        alt={method.name}
                        className="w-10 h-10 rounded-lg object-contain bg-white"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <div className="text-left flex-1">
                      <p className="font-medium">{method.name}</p>
                      <p className="text-xs text-white/50">
                        {method.processingTime} • Fee: {method.feeType === 'percentage' ? `${method.fee}%` : `${method.fee} TON`}
                      </p>
                    </div>
                    {selectedMethod?._id === method._id && (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
